/** Iframe-safe clipboard copy.
 *
 *  Hosted preview iframes (e.g. dev preview environments) may deny the
 *  `clipboard-write` permission, which makes `navigator.clipboard.writeText()`
 *  reject with NotAllowedError. Calling it without a guard surfaces an
 *  unhandled-promise-rejection red runtime overlay in dev mode and breaks
 *  the UI in prod.
 *
 *  This helper tries the modern API only when the permission allows it, then
 *  falls back to the legacy `document.execCommand('copy')` trick, and finally
 *  to a `window.prompt()` so the user can copy manually. Always returns a
 *  resolved boolean — never throws.
 */
export async function safeCopy(text) {
  try {
    if (
      navigator.clipboard &&
      window.isSecureContext &&
      typeof navigator.permissions?.query === 'function'
    ) {
      try {
        const p = await navigator.permissions.query({ name: 'clipboard-write' });
        if (p.state === 'granted' || p.state === 'prompt') {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (_) { /* permissions API absent — try writeText directly */ }
    }
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* fall through to legacy */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) return true;
  } catch (_) { /* fall through */ }
  try { window.prompt('Copy this value (Ctrl/Cmd-C):', text); } catch (_) { /* noop */ }
  return false;
}
